import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import _reduce from 'lodash/reduce';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { PlusOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Loader, Stack, Typography } from '@lyearn/ui';
import SelectSkillsDialog from '@/modules/skills/components/AddSkills/components/SelectSkillsDialog';
import useSkills from '@/modules/skills/hooks/useSkills';
import { SkillType } from '@/types/schema';

import SkillTag from './components/RemoveablePill';

function AddSkill({
  allowPartialUpdates,
  fetching,
  selectedSkills,
  onClick,
}: {
  allowPartialUpdates?: boolean;
  fetching?: boolean;
  selectedSkills: SkillType[];
  onClick: () => void;
}) {
  const { t } = useTranslation();

  if (allowPartialUpdates) {
    return selectedSkills.length === 0 ? (
      <Typography
        className="cursor-default ps-10"
        color="text-text-placeholder"
        variant="heading-xxs">
        {t('QuizCreator:QuestionsSettings.NoSkillAttached')}
      </Typography>
    ) : null;
  }

  return (
    <Stack alignItems="center" gapX={8}>
      <Stack
        alignItems="center"
        className={cn('h-32 rounded-full border border-border py-6 pe-12 ps-10 me-auto', {
          'cursor-pointer': !fetching,
        })}
        direction="row"
        gapX={4}
        onClick={!allowPartialUpdates && !fetching ? onClick : undefined}>
        <PlusOutline className="h-16 w-16" fillColorToken={'icon-secondary'} />
        <Typography color="text-text-placeholder" variant="body-short-02">
          {t('Skills:AddSkills.AddSkills')}
        </Typography>
      </Stack>
      {fetching ? <Loader /> : null}
    </Stack>
  );
}

function useResolvedSkills({
  selectedIds = [],
  quizSkillIds = [],
}: {
  selectedIds?: string[];
  quizSkillIds?: string[];
}) {
  const { skills, fetching, error } = useSkills({ ids: [...selectedIds, ...quizSkillIds] });

  const { selectedSkills, quizSkills } = React.useMemo(() => {
    const selectedIdsSet = new Set(selectedIds ?? []);
    const quizSkillIdsSet = new Set(quizSkillIds ?? []);

    return _reduce(
      skills,
      (result, skill) => {
        if (selectedIdsSet.has(skill.id)) {
          result.selectedSkills.push(skill as SkillType);
        }

        if (quizSkillIdsSet.has(skill.id)) {
          result.quizSkills.push(skill as SkillType);
        }

        return result;
      },
      {
        selectedSkills: [] as SkillType[],
        quizSkills: [] as SkillType[],
      },
    );
  }, [quizSkillIds, skills, selectedIds]);

  return {
    selectedSkills,
    quizSkills,
    fetching,
    error,
  };
}

interface SkillMeasuredProps {
  value?: string[];
  onChange: (nextValue: string[]) => void;
  allowPartialUpdates?: boolean;
  quizSkillIds?: string[];
}

const SkillMeasured: React.FC<SkillMeasuredProps> = (props) => {
  const { value, onChange, allowPartialUpdates, quizSkillIds } = props;

  const { t } = useTranslation();
  const [open, { toggle, off }] = useToggle();

  const { selectedSkills, quizSkills, fetching } = useResolvedSkills({
    selectedIds: value,
    quizSkillIds,
  });

  const onUpdateSkill = React.useCallback(
    (nextValue?: string[]) => {
      if (!allowPartialUpdates && nextValue?.length) {
        onChange(nextValue);
      }
    },
    [allowPartialUpdates, onChange],
  );

  const onRemoveSkill = React.useCallback(
    (id: string) => {
      if (!allowPartialUpdates) {
        onChange(value?.filter((it) => it !== id) ?? []);
      }
    },
    [allowPartialUpdates, onChange, value],
  );

  return (
    <Stack direction="col">
      <Typography className="mb-16 ps-8" variant="body-short-02">
        {t('Common:SkillDetails.Skill')}
      </Typography>
      <AddSkill
        allowPartialUpdates={allowPartialUpdates}
        fetching={fetching}
        selectedSkills={selectedSkills}
        onClick={toggle}
      />
      {open ? (
        <SelectSkillsDialog
          description={t('QuizCreator:Form.SkillDescription')}
          filteredSkillIds={quizSkillIds}
          primaryActionText={t('Common:Actions.Save')}
          selectedSkillIds={value}
          title={t('Common:Form.Skills')}
          onAttach={onUpdateSkill}
          onClose={off}
        />
      ) : null}
      {_isEmpty(value) || fetching ? null : (
        <Stack className="mt-12 flex w-full flex-1 flex-wrap content-start" gapX={8} gapY={12}>
          {selectedSkills?.map((skill) => {
            const { id, name } = skill;
            return (
              <SkillTag
                key={id}
                allowUpdates={!Boolean(allowPartialUpdates)}
                id={id}
                name={name}
                onRemove={onRemoveSkill}
              />
            );
          })}
        </Stack>
      )}
      {_isEmpty(quizSkills) || fetching ? null : (
        <Stack className="mt-12 flex w-full flex-1 flex-wrap content-start" gapX={8} gapY={12}>
          {quizSkills?.map((skill) => {
            const { id, name } = skill;
            return (
              <SkillTag
                key={id}
                allowUpdates={false}
                id={id}
                name={name}
                onRemove={onRemoveSkill}
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default React.memo(SkillMeasured) as React.FC<SkillMeasuredProps>;
